import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import type { SupabaseClient } from "@supabase/supabase-js";
import { AuthProvider, WorkspaceProvider, useAuth, useWorkspace } from "./lib/session";
import { ErrorState, Loading } from "./components/ui";
import { Layout, SetupShell } from "./components/Layout";
import { SignIn } from "./screens/SignIn";
import { CreateWorkspace } from "./screens/CreateWorkspace";
import { isPublicPath } from "./lib/happiness";
import { allModuleRoutes } from "./modules/routes";
import { HOME_PATH, REDIRECTS, redirectTarget, setupPathForOnboarding } from "./lib/nav";
import { applyThemePref, readThemePref } from "./lib/themePref";
import { clearClaim, pendingClaim, rememberClaim } from "./lib/claim";
import { useEffect, useState } from "react";
import { Notice } from "./components/ui";
import { screen } from "./modules/screen";
import { opened } from "./modules/routes";

// Screens load when first opened, so the first page fetches only the shell and itself (#562). The signed-in pages
// come from the destinations' registries (src/modules); these are the screens used before going live.
const Connections = screen(() => import("./screens/settings/Sections"), "Connections");
const Studio = screen(() => import("./screens/website/Studio"), "Studio");
const OpenOgma = screen(() => import("./screens/setup/OpenOgma"), "OpenOgma");
const HowDidWeDo = screen(() => import("./screens/HowDidWeDo"), "HowDidWeDo");

// The theme this browser chose in Personal settings (light unless changed), applied before the first paint.
applyThemePref(readThemePref());
// 4.19 A preview website claimed on its asset page arrives as ?claim=<id>: kept until the workspace exists.
if (typeof window !== "undefined") rememberClaim(window.location.search);

/** 4.19 Brings a claimed preview website into the studio once the workspace exists, and says so. */
export function ClaimImport() {
  const ws = useWorkspace();
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  useEffect(() => {
    const id = pendingClaim();
    if (ws.status !== "ready" || !ws.crm || !id) return;
    let live = true;
    ws.crm
      .importClaimedSite(id)
      .then(() => {
        clearClaim();
        if (live) setMsg({ text: "Your preview website is in the website studio as a draft. Check it and publish it when you are ready.", tone: "ok" });
      })
      .catch((e: unknown) => {
        clearClaim();
        if (live) setMsg({ text: e instanceof Error ? e.message : "We couldn't bring your preview website across.", tone: "bad" });
      });
    return () => {
      live = false;
    };
  }, [ws.status, ws.crm]);
  return msg ? <Notice text={msg.text} tone={msg.tone} /> : null;
}

/** An old path's new home, keeping its id, query string and #section, so old links and emails still work. */
export function Redirect({ to }: { to: string }) {
  const params = useParams();
  const { search, hash } = useLocation();
  // The new home may carry its own query and #section (`/conversations/inbox?tab=replies`, `…/after-reply#defaults`).
  const [target, ownHash = ""] = redirectTarget(to, params).split("#") as [string, string | undefined];
  const [path, ownQuery] = target.split("?") as [string, string | undefined];
  const q = [ownQuery, search.replace(/^\?/, "")].filter(Boolean).join("&");
  return <Navigate to={`${path}${q ? `?${q}` : ""}${hash || (ownHash ? `#${ownHash}` : "")}`} replace />;
}

/** An old wizard step's address: the closest guided set-up step or part screen, keeping the query string. */
function OldStepRedirect({ onboarded }: { onboarded: boolean }) {
  const { step } = useParams<{ step?: string }>();
  const { search, hash } = useLocation();
  return <Navigate to={`${setupPathForOnboarding(step, onboarded)}${search}${hash}`} replace />;
}

function Signed() {
  const ws = useWorkspace();
  if (ws.status === "loading") return <Loading label="Opening your workspace" />;
  if (ws.status === "error") return <ErrorState error={ws.error ?? "Something went wrong."} onRetry={() => void ws.refresh()} />;
  if (ws.status === "none") return <CreateWorkspace />;
  const onboarded = Boolean(ws.membership?.onboarded_at);
  return (
    <>
    <ClaimImport />
    <Routes>
      {/* The old set-up wizard is retired: its addresses open the closest guided set-up step or part screen. */}
      <Route path="/onboarding" element={<OldStepRedirect onboarded={onboarded} />} />
      <Route path="/onboarding/:step" element={<OldStepRedirect onboarded={onboarded} />} />
      {onboarded ? (
        <Route element={<Layout />}>
          {/* Ask Ogma, Campaigns, Conversations, Quotes, Contacts, Results and Settings: built from their registries. */}
          <Route index element={<Navigate to={HOME_PATH} replace />} />
          {allModuleRoutes()}
          {/* Every old address opens its new home (lib/nav.ts MOVED). */}
          {REDIRECTS.map(([from, to]) => (
            <Route key={from} path={from} element={<Redirect to={to} />} />
          ))}
          <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
        </Route>
      ) : (
        <>
          {/* Before going live (Craig, 31 Oct 2026): the business name (CreateWorkspace), then Ogma opens and walks through
              the rest in Ask Ogma (OpenOgma); the old set-up steps' addresses open it. */}
          <Route path="/setup/*" element={<Navigate to="/setup" replace />} />
          <Route element={<SetupShell />}>
            <Route path="/setup" element={opened(<OpenOgma />)} />
            {/* The website part's "Build my site" opens the studio before going live, at its address after going live
                (Attract › Actions › Website), with the site's address and domain as well since Accounts is not open yet. */}
            <Route path="/attract/actions/website" element={opened(<Studio />)} />
            {/* 0.12 Instagram Login returns to /company/accounts?instagram=pending&n=<nonce> (oauth-instagram). Before
                going live that address must open too: the catch-all below drops the query string, so the held token
                would never be finished and would expire. */}
            <Route path="/settings/connections" element={opened(<Connections />)} />
          </Route>
          <Route path="*" element={<Navigate to="/setup" replace />} />
        </>
      )}
    </Routes>
    </>
  );
}

function Gate() {
  const { ready, session } = useAuth();
  if (!ready) return <Loading label="Signing you in" />;
  if (!session) return <SignIn />;
  return (
    <WorkspaceProvider>
      <Signed />
    </WorkspaceProvider>
  );
}

export function App({ client }: { client: SupabaseClient }) {
  // 10.19 The happiness check's answer page is public: no sign-in, no workspace (screens/HowDidWeDo.tsx).
  if (typeof window !== "undefined" && isPublicPath(window.location.pathname)) return opened(<HowDidWeDo />);
  return (
    <AuthProvider client={client}>
      <BrowserRouter>
        <Gate />
      </BrowserRouter>
    </AuthProvider>
  );
}
